import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AdminPanelSettings, Login as LoginIcon } from '@mui/icons-material';
import adminService from '../services/adminService';

const Login = ({ setCurrentUser }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [credentials, setCredentials] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value,
    });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await adminService.login(credentials);
      
      // Get user details
      const userDetails = await adminService.getCurrentUser();
      
      // Check if user is admin/staff
      if (!userDetails.is_staff && !userDetails.is_superuser) {
        throw new Error('אין לך הרשאות ניהול');
      }

      setCurrentUser(userDetails);
      navigate('/analytics', { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      setError(
        error.response?.data?.detail || 
        error.message || 
        'שגיאה בהתחברות. אנא נסה שוב.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? 2 : 3,
      }}
    >
      <Container 
        component="main" 
        maxWidth={isSmallScreen ? "xs" : "sm"}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Card
          elevation={10}
          sx={{
            width: '100%',
            maxWidth: isSmallScreen ? 360 : 450,
            borderRadius: 3,
            overflow: 'hidden',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              color: 'white',
              py: isMobile ? 3 : 4,
              px: isMobile ? 2 : 3,
              textAlign: 'center',
            }}
          >
            <AdminPanelSettings 
              sx={{ 
                fontSize: isMobile ? '3rem' : '4rem',
                mb: 1,
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
              }} 
            />
            <Typography 
              component="h1" 
              variant={isMobile ? "h5" : "h4"}
              sx={{ 
                fontWeight: 'bold',
                mb: 0.5,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              Fit Switch
            </Typography>
            <Typography 
              variant={isMobile ? "body1" : "h6"}
              sx={{ 
                opacity: 0.9,
                fontWeight: 300,
                textShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}
            >
              מערכת ניהול מתקדמת
            </Typography>
          </Box>

          <CardContent sx={{ p: isMobile ? 3 : 4 }}>
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              sx={{ 
                mb: 3, 
                textAlign: 'center',
                color: 'primary.main',
                fontWeight: 'bold'
              }}
            >
              התחברות למערכת
            </Typography>

            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  width: '100%', 
                  mb: 3,
                  borderRadius: 2,
                  fontSize: isMobile ? '0.875rem' : '1rem'
                }}
              >
                {error}
              </Alert>
            )}

            <Box 
              component="form" 
              onSubmit={handleSubmit} 
              sx={{ width: '100%' }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="כתובת מייל"
                name="email"
                autoComplete="email"
                autoFocus
                value={credentials.email}
                onChange={handleChange}
                disabled={loading}
                size={isMobile ? "medium" : "large"}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: isMobile ? '1rem' : '1.1rem',
                  },
                }}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="סיסמה"
                type="password"
                id="password"
                autoComplete="current-password"
                value={credentials.password}
                onChange={handleChange}
                disabled={loading}
                size={isMobile ? "medium" : "large"}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: isMobile ? '1rem' : '1.1rem',
                  },
                }}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size={isMobile ? "large" : "large"}
                disabled={loading}
                startIcon={loading ? null : <LoginIcon />}
                sx={{
                  mt: 2,
                  mb: 2,
                  py: isMobile ? 1.5 : 2,
                  fontSize: isMobile ? '1rem' : '1.1rem',
                  fontWeight: 'bold',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  boxShadow: '0 4px 15px rgba(25, 118, 210, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    boxShadow: '0 6px 20px rgba(25, 118, 210, 0.6)',
                    transform: 'translateY(-2px)',
                  },
                  '&:disabled': {
                    background: 'linear-gradient(135deg, #ccc 0%, #999 100%)',
                    boxShadow: 'none',
                    transform: 'none',
                  },
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    <span>מתחבר...</span>
                  </Box>
                ) : (
                  'התחבר למערכת'
                )}
              </Button>
            </Box>

            {/* Footer */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ 
                  fontSize: isMobile ? '0.8rem' : '0.875rem',
                  fontStyle: 'italic'
                }}
              >
                גישה מורשית בלבד • Fit Switch Admin Panel
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Login;
